/**
 * Ticker Strip — hero copy + live news-style scrolling chips at the bottom.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const tickerStrip: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const subhead = inputs.subtext ?? "";
  const chips = (inputs.items?.length ? inputs.items : ["BERLIN +28%", "WIEN +41%", "ZÜRICH +33%", "MÜNCHEN +52%"]).slice(0, 16);
  const speedPxS = Math.max(60, parseFloat(inputs.number ?? "220"));

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const xs = typeSize("xs", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const stripId = `${clipPrefix}-strip`;
  const trackId = `${clipPrefix}-track`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const chipHtml = chips.map((c) => `<div style="display:inline-flex;align-items:center;gap:10px;padding:14px 22px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(accent, 0.4)};color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.75}px;letter-spacing:1.5px;white-space:nowrap;">
    <span style="width:8px;height:8px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${withAlpha(accent, 0.6)};"></span>
    <span>${escapeHtml(c)}</span>
  </div>`).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding:80px 64px;gap:24px;">
    <div style="display:flex;align-items:center;gap:12px;align-self:flex-start;padding:8px 16px;border-radius:9999px;background:${withAlpha("#dc2626", 0.15)};border:1.5px solid ${withAlpha("#dc2626", 0.55)};color:#fca5a5;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${xs * 0.78}px;letter-spacing:4px;text-transform:uppercase;">
      <span id="${clipPrefix}-blink" style="width:10px;height:10px;border-radius:9999px;background:#ef4444;box-shadow:0 0 12px #ef4444;"></span>
      <span>LIVE</span>
      <span style="opacity:0.55;">·</span>
      <span>Allyver Markets</span>
    </div>
    <div style="display:flex;flex-direction:column;align-items:center;gap:18px;text-align:center;max-width:1080px;flex:1;justify-content:center;">
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.85}px;letter-spacing:-2.5px;color:${themeColors.fg};line-height:1.0;text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
      ${subhead ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${md * 0.6}px;color:${accent};letter-spacing:2px;text-transform:uppercase;">${escapeHtml(subhead)}</div>` : ""}
    </div>
    <div id="${stripId}" style="width:calc(100% + 128px);margin:0 -64px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.04)} 0%, ${withAlpha(themeColors.bg, 0.6)} 100%);border-top:1px solid ${withAlpha(accent, 0.35)};border-bottom:1px solid ${withAlpha(accent, 0.35)};overflow:hidden;padding:18px 0;mask:linear-gradient(90deg, transparent, black 6%, black 94%, transparent);-webkit-mask:linear-gradient(90deg, transparent, black 6%, black 94%, transparent);">
      <div id="${trackId}" style="display:inline-flex;gap:12px;white-space:nowrap;will-change:transform;">${chipHtml}${chipHtml}</div>
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 18 }) : "",
    subhead ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${stripId}`, { opacity: 0, y: 30 }),
    gsapSetLine(`#${trackId}`, { x: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${stripId}`, { opacity: 1, y: 0, duration: 0.65, ease: "raw:\"power3.out\"" }, 0.1),
    // Scroll the track; approximate width per chip ~ 200px.
    `tl.to(${JSON.stringify("#" + trackId)},{x:-${chips.length * 200},duration:${(chips.length * 200) / speedPxS},ease:"none",repeat:-1},${clipStartSec.toFixed(4)});`,
    `tl.to(${JSON.stringify("#" + clipPrefix + "-blink")},{opacity:0.3,duration:0.5,repeat:-1,yoyo:true,ease:"sine.inOut"},${clipStartSec.toFixed(4)});`,
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.25));
  if (subhead) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.5));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
